import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker, Pane, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import type { POI, LocalType, City } from '../types';
import { getMarkerStyle } from '../constants';
import { POIPopup } from './POIPopup';
import { MenuControl } from './MenuControl';
import { LocationControl } from './LocationControl';
import { SearchControl } from './SearchCity';
import { FilterControls } from './FilterControls';
import { LegalDisclaimer } from './LegalDisclaimer';
import { RegionPickerModal } from './RegionPickerModal';
import { PaywallModal } from './PaywallModal';
import { useUnlock, isPoiUnlocked, readUnlockState } from '../lib/unlock';
import { ITALIAN_REGIONS } from '../lib/regions';
import { purchaseUnlockAll, restoreUnlockAll, isUnlocked as iapIsUnlocked } from '../lib/iap';

interface ChangeViewProps {
  center: [number, number];
  zoom: number;
}
const ChangeView: React.FC<ChangeViewProps> = ({ center, zoom }) => {
  const map = useMap();
  // FIX importante: dipendiamo da valori NUMERICI primitivi, non dall'array
  // `center` (ogni render React crea un nuovo array → useEffect ritriggera →
  // setView resetta la mappa anche quando le coordinate non sono cambiate,
  // causando uno zoom-out indesiderato al click su un marker).
  const lat = center[0];
  const lng = center[1];
  useEffect(() => {
    map.setView([lat, lng], zoom);
  }, [lat, lng, zoom, map]);
  return null;
};

/**
 * Centra la mappa sulla regione gratis scelta dall'utente.
 *
 * Trigger:
 *  - Al primo set della regione (onboarding completato): flyTo animato
 *  - Al refresh dell'app (regione gia' settata + non purchasedAll): jump
 *    immediato per non avere il flash "Italia → regione"
 *
 * Se purchasedAll, non ricentra: l'utente ha accesso a tutto, mantiene la
 * vista corrente.
 */
interface RegionFocusProps {
  region: string | null;
  purchasedAll: boolean;
}
const RegionFocus: React.FC<RegionFocusProps> = ({ region, purchasedAll }) => {
  const map = useMap();
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (purchasedAll || !region) return;
    const info = ITALIAN_REGIONS.find(r => r.name === region);
    if (!info) return;
    if (firstRunRef.current) {
      // Primo mount con regione gia' settata (refresh): jump immediato
      map.setView([info.center.lat, info.center.lng], info.zoom);
      firstRunRef.current = false;
    } else {
      // Cambio successivo (onboarding appena completato): animazione fluida
      map.flyTo([info.center.lat, info.center.lng], info.zoom, { duration: 1.2 });
    }
  }, [region, purchasedAll, map]);
  return null;
};

// Memoization globale delle icone: chiave = poi.type + locked.
// Solo ~20 istanze DivIcon distinte (10 tipi × 2 stati locked/unlocked).
//
// Le icone hanno classi CSS specifiche (poi-pin-dim, poi-lock-badge) cosi'
// possiamo rimuoverle imperativamente dal DOM dopo l'acquisto, senza
// passare attraverso React re-render.
const iconCache = new Map<string, L.DivIcon>();
const createCustomIcon = (poi: POI, locked: boolean = false) => {
    const cacheKey = `${poi.type}-${locked ? "L" : "U"}`;
    const cached = iconCache.get(cacheKey);
    if (cached) return cached;
    const style = getMarkerStyle(poi);
    const Icon = style.icon;
    // POI bloccato: opacita' ridotta + piccolo lucchetto in alto a destra
    const dimCls = locked ? "poi-pin-dim opacity-50" : "";
    const iconHtml = ReactDOMServer.renderToString(
        <div className={`relative w-10 h-12 flex items-center justify-center ${dimCls}`}>
            <div className={`w-10 h-10 rounded-full ${style.color} flex items-center justify-center shadow-lg border-2 border-white/80`}>
                <span style={{ filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.3))', display: 'inline-flex' }}>
                    <Icon className="w-6 h-6 text-white" />
                </span>
            </div>
            <div
                className="absolute top-[36px] w-0 h-0"
                style={{
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: `10px solid ${style.pinColor}`,
                    filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))'
                }}
            ></div>
            {locked && (
                <div className="poi-lock-badge absolute -top-1 -right-1 bg-gray-800 rounded-full w-4 h-4 flex items-center justify-center border border-white">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                    </svg>
                </div>
            )}
        </div>
    );

    const icon = new L.DivIcon({
        html: iconHtml,
        className: 'custom-leaflet-icon',
        iconSize: [40, 48],
        iconAnchor: [20, 48],
        popupAnchor: [0, -52],
    });
    iconCache.set(cacheKey, icon);
    return icon;
};

/**
 * Lista marker memoizzata in modo aggressivo.
 *
 * Il bottleneck del freeze post-acquisto era la valutazione + reconciliation
 * di 1200+ <Marker> React elements quando lo stato unlock cambiava. Su
 * WKWebView iOS bloccava il main thread per secondi → white screen.
 *
 * Soluzione: MarkersLayer dipende SOLO da `pois` e `freeRegion`. Quando
 * l'utente acquista (purchasedAll true), MarkersLayer NON si re-renderizza.
 * I lock badge vengono rimossi imperativamente dal DOM in chunks di 50 per
 * frame (vedi useEffect in MapComponent), senza far passare React.
 *
 * Lo stato del Popup (lock screen vs contenuto reale POI) e' invece sempre
 * letto fresh dal POIPopup via prop `isLocked`. Ma siccome il Popup esiste
 * lazy (solo quando aperto, max 1 alla volta), questo non causa overhead.
 */
/**
 * Contenuto del Popup di un singolo POI.
 *
 * IMPORTANTE: NON usa useUnlock per evitare 1200+ subscriber simultanei
 * che freezavano WKWebView all'acquisto. Legge lo stato fresh via
 * readUnlockState() (lettura sincrona da localStorage, senza subscription).
 *
 * Si re-mounta automaticamente ad ogni apertura del popup grazie alla key
 * basata su openTick passata da MarkerWithPopup (vedi sotto), cosi' lo
 * stato locked viene sempre ricalcolato fresh quando l'utente apre il
 * popup dopo aver acquistato.
 */
interface PopupContentProps {
  poi: POI;
  onUnlockClick: () => void;
}
const PopupContent: React.FC<PopupContentProps> = ({ poi, onUnlockClick }) => {
  // Lettura sincrona one-shot. Nessuna subscription = nessun re-render
  // a cascata quando 1200 componenti diventerebbero "unlocked" insieme.
  const state = readUnlockState();
  const locked = !isPoiUnlocked(poi.region, state);
  return (
    <POIPopup poi={poi} isLocked={locked} onUnlockClick={onUnlockClick} />
  );
};

/**
 * Singolo Marker con il suo Popup.
 *
 * State locale `openTick`: incrementato ogni volta che il popup si apre.
 * Usato come `key` per PopupContent → ad ogni apertura il PopupContent
 * viene re-mountato → legge lo stato unlock fresh.
 *
 * Beneficio chiave: il re-render avviene SOLO per il marker il cui popup
 * si apre, non per tutti i 1200 marker. Le state changes globali (es.
 * acquisto) non triggerano nessun re-render qui dentro.
 */
interface MarkerWithPopupProps {
  poi: POI;
  initialLocked: boolean;
  onUnlockClick: () => void;
  markerRefs: React.MutableRefObject<Record<string, L.Marker | null>>;
}
const MarkerWithPopup: React.FC<MarkerWithPopupProps> = React.memo(({
  poi,
  initialLocked,
  onUnlockClick,
  markerRefs,
}) => {
  const [openTick, setOpenTick] = useState(0);
  return (
    <Marker
      position={[poi.coordinates.lat, poi.coordinates.lng]}
      icon={createCustomIcon(poi, initialLocked)}
      ref={(el) => {
        if (el) markerRefs.current[poi.id] = el;
      }}
      eventHandlers={{
        // Quando il popup si apre, incrementa il tick → PopupContent
        // si re-monta → legge stato unlock fresh da localStorage.
        popupopen: () => setOpenTick((t) => t + 1),
      }}
    >
      <Popup autoPan={false}>
        <PopupContent
          key={openTick}
          poi={poi}
          onUnlockClick={onUnlockClick}
        />
      </Popup>
    </Marker>
  );
});

interface MarkersLayerProps {
  pois: POI[];
  freeRegion: string | null;
  onUnlockClick: () => void;
  markerRefs: React.MutableRefObject<Record<string, L.Marker | null>>;
}
const MarkersLayer: React.FC<MarkersLayerProps> = React.memo(({
  pois,
  freeRegion,
  onUnlockClick,
  markerRefs,
}) => {
  // Leggi purchasedAll fresh da localStorage SENZA subscription. Cosi':
  //   - Al primo avvio dopo restart, se l'utente ha gia' acquistato,
  //     initialLocked = false per tutti → niente lucchetti.
  //   - Durante la sessione, se purchasedAll cambia (es. nuovo acquisto),
  //     MarkersLayer non si re-renderizza (memo, freeRegion non cambia)
  //     → niente freeze. I lucchetti esistenti vengono rimossi imperativamente
  //     dall'useEffect in MapComponent.
  const purchasedAll = readUnlockState().purchasedAll;
  return (
    <>
      {pois.map((poi) => {
        const initialLocked = !purchasedAll &&
          (!freeRegion || poi.region?.toLowerCase() !== freeRegion.toLowerCase());
        return (
          <MarkerWithPopup
            key={poi.id}
            poi={poi}
            initialLocked={initialLocked}
            onUnlockClick={onUnlockClick}
            markerRefs={markerRefs}
          />
        );
      })}
    </>
  );
});

/**
 * Tracker che notifica al parent quando un popup Leaflet apre/chiude,
 * e auto-centra la mappa sul marker cliccato spostando il marker verso
 * la parte BASSA della viewport (perche' il popup compare sopra il
 * marker — cosi' c'e' spazio in alto per renderlo visibile per intero).
 *
 * NB: l'autoPan di default di Leaflet e' disabilitato sui Popup (via
 * `autoPan={false}` nel JSX), perche' fa un pan minimal che non centra
 * in modo prevedibile. Qui usiamo un pan custom piu' "presentabile".
 */
interface PopupTrackerProps {
  onChange: (isOpen: boolean) => void;
}
const PopupTracker: React.FC<PopupTrackerProps> = ({ onChange }) => {
  const map = useMap();
  const observerRef = useRef<ResizeObserver | null>(null);
  const resizeDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!map) return;

    /**
     * Calcola se serve un pan e lo applica.
     * Chiamata sia al popupopen iniziale sia ad ogni resize del popup
     * (es. apertura AI Assistant che fa crescere il contenuto).
     */
    const computeAndApplyPan = (popup: L.Popup) => {
      const popupEl = popup.getElement();
      const latlng = popup.getLatLng();
      if (!popupEl || !latlng) return;

      const popupHeight = popupEl.offsetHeight;
      const popupWidth = popupEl.offsetWidth;
      const markerPixel = map.latLngToContainerPoint(latlng);
      const { x: containerWidth, y: containerHeight } = map.getSize();
      // popupAnchor [0, -52] nel createCustomIcon → popup compare 52px
      // sopra il pixel del marker, centrato orizzontalmente.
      const POPUP_ANCHOR_OFFSET_Y = 52;
      const SAFE_TOP = 60;
      const SAFE_BOTTOM = 40;
      const SAFE_LEFT = 16;
      const SAFE_RIGHT = 16;

      // Asse Y
      const popupBottom = markerPixel.y - POPUP_ANCHOR_OFFSET_Y;
      const popupTop = popupBottom - popupHeight;
      let panY = 0;
      if (popupTop < SAFE_TOP) panY = popupTop - SAFE_TOP;
      else if (popupBottom > containerHeight - SAFE_BOTTOM) panY = popupBottom - (containerHeight - SAFE_BOTTOM);

      // Asse X
      const popupLeft = markerPixel.x - popupWidth / 2;
      const popupRight = markerPixel.x + popupWidth / 2;
      let panX = 0;
      if (popupLeft < SAFE_LEFT) panX = popupLeft - SAFE_LEFT;
      else if (popupRight > containerWidth - SAFE_RIGHT) panX = popupRight - (containerWidth - SAFE_RIGHT);

      if (Math.abs(panX) > 5 || Math.abs(panY) > 5) {
        map.panBy([panX, panY], { animate: true, duration: 0.3 });
      }
    };

    const handleOpen = (e: L.LeafletEvent) => {
      onChange(true);
      const popup = (e as L.PopupEvent).popup;
      if (!popup) return;

      // Pan iniziale dopo il render
      requestAnimationFrame(() => computeAndApplyPan(popup));

      // Attacca ResizeObserver: se il contenuto del popup cambia
      // dimensione (es. apertura AI Assistant), ricalcola il pan con
      // debounce di 120ms per evitare loop durante l'animazione stessa.
      const popupEl = popup.getElement();
      if (popupEl && typeof ResizeObserver !== "undefined") {
        observerRef.current?.disconnect();
        observerRef.current = new ResizeObserver(() => {
          if (resizeDebounceRef.current !== null) {
            window.clearTimeout(resizeDebounceRef.current);
          }
          resizeDebounceRef.current = window.setTimeout(() => {
            computeAndApplyPan(popup);
          }, 120);
        });
        observerRef.current.observe(popupEl);
      }
    };

    const handleClose = () => {
      onChange(false);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (resizeDebounceRef.current !== null) {
        window.clearTimeout(resizeDebounceRef.current);
        resizeDebounceRef.current = null;
      }
    };

    map.on("popupopen", handleOpen);
    map.on("popupclose", handleClose);
    return () => {
      map.off("popupopen", handleOpen);
      map.off("popupclose", handleClose);
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (resizeDebounceRef.current !== null) {
        window.clearTimeout(resizeDebounceRef.current);
      }
    };
  }, [map, onChange]);
  return null;
};

// Manages the z-index of the popup pane to ensure it's on top of controls
const PopupZIndexManager: React.FC = () => {
  const map = useMap();
  React.useEffect(() => {
    const popupPane = map.getPane('popupPane');
    if (popupPane) {
      popupPane.style.zIndex = '1001';
    }
  }, [map]);
  return null;
};

interface MapControlsProps {
  isPanelOpen: boolean;
  onTogglePanel: () => void;
  onLocateUser: () => void;
  onSearch: (query: string) => void;
  onSelect: (item: City | POI) => void;
  pois: POI[];
  activeFilters: LocalType[];
  onFilterChange: (type: LocalType) => void;
  isHidden?: boolean;
}

const MapControls: React.FC<MapControlsProps> = ({ isPanelOpen, onTogglePanel, onLocateUser, onSearch, onSelect, pois, activeFilters, onFilterChange, isHidden }) => {
  const [isLegalOpen, setIsLegalOpen] = useState(false);
  return (
    <div
      className={`absolute left-4 z-[500] flex flex-col items-start gap-2 map-controls-fade ${isHidden ? "popup-open" : ""}`}
      // Top:
      //   - iOS: env(safe-area-inset-top) ~44-59px (notch) + 16px = sotto notch
      //   - Android: env=0 (WebView gia' sotto status bar grazie a
      //     setOverlaysWebView(false) in native-init) + 16px = poco sotto
      //     status bar, cliccabile comodamente
      //   - Web: env=0 + 16px = standard top-4
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
    >
      <div className="flex items-start gap-2">
          <MenuControl isOpen={isPanelOpen} onToggle={onTogglePanel} />
          <LocationControl onLocateUser={onLocateUser} />
      </div>

      {isPanelOpen && (
          <div className="bg-white bg-opacity-90 backdrop-blur-md p-4 rounded-lg shadow-lg border border-gray-200 flex flex-col gap-4 w-full max-w-xs sm:max-w-sm animate-fade-in-down">
              {/* Brand header */}
              <div className="w-full pb-2 border-b border-gray-200 flex items-center gap-3">
                  <img
                    src="/icon-192.png"
                    alt="Itinere"
                    className="w-10 h-10 rounded-lg shadow-sm flex-shrink-0"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                  <div className="flex-grow min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight leading-none" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
                      Itinere
                    </h1>
                    <p className="text-xs text-gray-500 mt-1">Tutte le guide in una sola mappa</p>
                  </div>
              </div>
              <div className="w-full">
                  <SearchControl onSearch={onSearch} onSelect={onSelect} pois={pois} />
              </div>
              <div className="w-full">
                  <FilterControls activeFilters={activeFilters} onFilterChange={onFilterChange} />
              </div>
              <div className="w-full pt-1 border-t border-gray-200 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setIsLegalOpen(true)}
                    className="text-xs text-gray-500 hover:text-cyan-600 transition-colors underline-offset-2 hover:underline"
                  >
                    Note legali e attribuzioni
                  </button>
              </div>
          </div>
      )}

      <LegalDisclaimer isOpen={isLegalOpen} onClose={() => setIsLegalOpen(false)} />
    </div>
  );
};


interface MapComponentProps {
  pois: POI[];
  allPois: POI[];
  mapView: {
    coordinates: { lat: number; lng: number };
    zoom: number;
  };
  userLocation: { lat: number; lng: number } | null;
  selectedPoiId: string | null;
  isPanelOpen: boolean;
  onTogglePanel: () => void;
  onLocateUser: () => void;
  onSearch: (query: string) => void;
  onSelect: (item: City | POI) => void;
  activeFilters: LocalType[];
  onFilterChange: (type: LocalType) => void;
}

const MapEffects: React.FC<{
  selectedPoiId: string | null,
  markerRefs: React.MutableRefObject<Record<string, L.Marker | null>>
}> = ({ selectedPoiId, markerRefs }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedPoiId) return;

    const openPopup = () => {
      const marker = markerRefs.current[selectedPoiId];
      if (marker) {
        marker.openPopup();
      }
      map.off('moveend', openPopup);
    };

    map.on('moveend', openPopup);

    return () => {
      map.off('moveend', openPopup);
    };
  }, [selectedPoiId, map, markerRefs]);

  return null;
};


export const MapComponent: React.FC<MapComponentProps> = ({ pois, allPois, mapView, userLocation, selectedPoiId, onSelect, ...controlProps }) => {
  const markerRefs = useRef<Record<string, L.Marker | null>>({});
  const { state: unlockState, setFreeRegion, markPurchasedAll, restorePurchases } = useUnlock();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);

  // Onboarding al primo avvio: se non e' stata scelta nessuna regione gratis
  // E non e' stato acquistato il bundle, mostra il picker (bloccante).
  const showOnboarding = !unlockState.freeRegion && !unlockState.purchasedAll;

  // Callback stabile per non invalidare il React.memo del MarkersLayer ad
  // ogni render del MapComponent.
  const openPaywall = useCallback(() => setPaywallOpen(true), []);

  const handlePurchase = async () => {
    // Su native (iOS/Android) con RevenueCat configurato: chiamata reale.
    // Su web o senza credenziali RevenueCat: simulazione interna a iap.ts.
    const success = await purchaseUnlockAll();
    if (!success) {
      // L'utente ha annullato lo sheet di acquisto: no error, no notifica
      throw new Error("Acquisto annullato");
    }
    // Chiudi popup aperti PRIMA di marcare l'acquisto: altrimenti su iOS
    // WKWebView l'espansione del popup (da lock-screen a contenuto POI
    // completo con tab AI) avviene mentre il PaywallModal e' ancora visibile
    // e il re-render dei 1200+ marker provoca freeze / white screen.
    Object.values(markerRefs.current).forEach((m) => {
      try { m?.closePopup?.(); } catch { /* noop */ }
    });
    // Cede un frame al browser per processare la chiusura del popup prima
    // di triggerare il cambio di stato unlock (che re-renderizza i marker).
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
    markPurchasedAll();
  };

  const handleRestore = async () => {
    const success = await restoreUnlockAll();
    if (!success) {
      throw new Error("Nessun acquisto trovato da ripristinare.");
    }
    // Stesso pattern di handlePurchase: chiudi popup + yield prima di
    // aggiornare lo stato, per evitare freeze su iOS WKWebView.
    Object.values(markerRefs.current).forEach((m) => {
      try { m?.closePopup?.(); } catch { /* noop */ }
    });
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
    markPurchasedAll();
  };

  // Al boot: se siamo su native, verifica con RevenueCat se l'utente ha
  // gia' acquistato (sync da store, utile dopo cambio device/reinstall)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const unlocked = await iapIsUnlocked();
        if (!cancelled && unlocked && !unlockState.purchasedAll) {
          markPurchasedAll();
        }
      } catch (e) {
        console.warn("[IAP] check at boot failed:", e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quando l'utente acquista, rimuovi i lock badge dal DOM imperativamente
  // in chunks di 50 per frame. Bypassiamo React per non innescare il
  // re-render dei 1200+ <Marker> elements che bloccava WKWebView su iOS.
  //
  // Effetti visibili:
  //   - lock badges scompaiono progressivamente in ~10-20 frames (300-600ms)
  //   - opacita' del pin torna piena (rimuove .opacity-50)
  //   - main thread non blocca, UI resta responsive
  useEffect(() => {
    if (!unlockState.purchasedAll) return;
    let cancelled = false;

    const removeNodes = (selector: string, action: (el: Element) => void) => {
      const nodes = Array.from(document.querySelectorAll(selector));
      if (nodes.length === 0) return;
      const CHUNK = 50;
      let i = 0;
      const processChunk = () => {
        if (cancelled) return;
        const end = Math.min(i + CHUNK, nodes.length);
        for (; i < end; i++) action(nodes[i]);
        if (i < nodes.length) requestAnimationFrame(processChunk);
      };
      requestAnimationFrame(processChunk);
    };

    // Rimuovi tutti i lucchetti dal DOM
    removeNodes('.leaflet-marker-icon .poi-lock-badge', (el) => el.remove());
    // Ripristina opacita' piena ai pin dimmed
    removeNodes('.leaflet-marker-icon .poi-pin-dim', (el) => {
      el.classList.remove('poi-pin-dim', 'opacity-50');
    });

    // Aggiorna anche le icone cached: cosi' i marker che entrano/escono dalla
    // viewport durante pan/zoom non riapplicano il lock vecchio.
    // (clear forza createCustomIcon a ricreare al prossimo uso con locked=false)
    // NB: react-leaflet riusa l'icona esistente sui marker gia' montati,
    // quindi non triggera setIcon massivo.
    iconCache.clear();

    return () => { cancelled = true; };
  }, [unlockState.purchasedAll]);

  return (
    <>
    <MapContainer
        center={[mapView.coordinates.lat, mapView.coordinates.lng]}
        zoom={mapView.zoom}
        scrollWheelZoom={true}
        zoomControl={false}
        className="h-screen w-screen z-0"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      <ZoomControl position="topright" />
      <ChangeView center={[mapView.coordinates.lat, mapView.coordinates.lng]} zoom={mapView.zoom} />
      <RegionFocus region={unlockState.freeRegion} purchasedAll={unlockState.purchasedAll} />

      <PopupZIndexManager />
      <PopupTracker onChange={setPopupVisible} />
      <MapControls {...controlProps} onSelect={onSelect} pois={allPois} isHidden={popupVisible} />

      <MapEffects selectedPoiId={selectedPoiId} markerRefs={markerRefs} />

      <MarkersLayer
        pois={pois}
        freeRegion={unlockState.freeRegion}
        onUnlockClick={openPaywall}
        markerRefs={markerRefs}
      />


      <Pane name="userLocationPulse" style={{ zIndex: 649 }} />
      <Pane name="userLocation" style={{ zIndex: 650 }} />
      {userLocation && (
          <>
            <CircleMarker
                center={[userLocation.lat, userLocation.lng]}
                radius={12}
                pathOptions={{
                    className: 'user-location-pulse',
                    color: '#22d3ee',
                    fillColor: '#22d3ee',
                    fillOpacity: 0.5,
                    weight: 0,
                }}
                pane="userLocationPulse"
            />
            <CircleMarker
                center={[userLocation.lat, userLocation.lng]}
                radius={8}
                pathOptions={{
                    color: '#fff',
                    fillColor: '#22d3ee',
                    fillOpacity: 1,
                    weight: 2,
                }}
                pane="userLocation"
            >
                <Popup>You are here</Popup>
            </CircleMarker>
          </>
      )}
    </MapContainer>

    {/* Onboarding al primo avvio: bloccante, sceglie la regione gratis */}
    <RegionPickerModal
      isOpen={showOnboarding}
      pois={allPois}
      onConfirm={(region) => setFreeRegion(region)}
    />

    {/* Paywall: si apre quando si clicca un POI di regione non sbloccata */}
    <PaywallModal
      isOpen={paywallOpen}
      freeRegion={unlockState.freeRegion}
      onClose={() => setPaywallOpen(false)}
      onPurchase={handlePurchase}
      onRestore={handleRestore}
    />
    </>
  );
};
